import { useEffect, useMemo, useState } from "react";
import { Button, PageHeader } from "@/components/ui";
import { Icon } from "@/components/IconSprite";
import { PermissionGate } from "@/components/PermissionGate";
import { usePermissions } from "@/hooks/usePermissions";
import { useBrandVoice, useUpdateBrandVoice } from "@/hooks/useBrandVoice";
import type { BrandVoicePayload } from "@/types/content";

const TONE_MAX_BYTES = 2_048;

interface ChipListEditorProps {
  label: string;
  hint?: string;
  danger?: boolean;
  values: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
}

const ChipListEditor = ({
  label,
  hint,
  danger,
  values,
  onChange,
  placeholder,
}: ChipListEditorProps) => {
  const [draft, setDraft] = useState("");

  const addCurrent = () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    if (values.includes(trimmed)) {
      setDraft("");
      return;
    }
    onChange([...values, trimmed]);
    setDraft("");
  };

  const remove = (item: string) => {
    onChange(values.filter((v) => v !== item));
  };

  const chipStyle = danger
    ? {
        borderColor: "var(--err-bd)",
        background: "var(--err-bg)",
        color: "var(--err)",
      }
    : undefined;

  return (
    <div>
      <div className="lbl-row">
        <label className="fl" style={danger ? { color: "var(--err)" } : undefined}>
          {label}
        </label>
        <span className="cnt">{values.length} items</span>
      </div>

      <div style={{ display: "flex", gap: 7, marginBottom: 10 }}>
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addCurrent();
            }
          }}
          placeholder={placeholder}
          className="inp"
        />
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={addCurrent}
          disabled={!draft.trim()}
        >
          Add
        </Button>
      </div>

      {values.length > 0 && (
        <div className="chips" style={hint ? { marginBottom: 8 } : undefined}>
          {values.map((item) => (
            <span key={item} className="chip" style={chipStyle}>
              {item}
              <button
                type="button"
                className="x"
                onClick={() => remove(item)}
                aria-label={`Remove ${item}`}
              >
                <Icon name="x" />
              </button>
            </span>
          ))}
        </div>
      )}

      {hint && <p className="hint">{hint}</p>}
    </div>
  );
};

const emptyDraft = (): BrandVoicePayload => ({
  tone: "",
  do_say: [],
  dont_say: [],
  banned_terms: [],
  signature_phrases: [],
  updated_at: null,
  updated_by: null,
});

const BrandVoice = () => {
  const { can } = usePermissions();
  const canEdit = can("brand_voice", "edit");
  const { data: loaded, isLoading } = useBrandVoice();
  const updateMutation = useUpdateBrandVoice();

  const [draft, setDraft] = useState<BrandVoicePayload>(emptyDraft());
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (loaded) {
      setDraft({
        tone: loaded.tone ?? "",
        do_say: [...(loaded.do_say ?? [])],
        dont_say: [...(loaded.dont_say ?? [])],
        banned_terms: [...(loaded.banned_terms ?? [])],
        signature_phrases: [...(loaded.signature_phrases ?? [])],
        updated_at: loaded.updated_at,
        updated_by: loaded.updated_by,
      });
    }
  }, [loaded]);

  const toneBytes = useMemo(() => new Blob([draft.tone]).size, [draft.tone]);
  const toneOver = toneBytes > TONE_MAX_BYTES;

  const handleReset = () => {
    if (!loaded) {
      setDraft(emptyDraft());
      return;
    }
    setDraft({
      tone: loaded.tone ?? "",
      do_say: [...(loaded.do_say ?? [])],
      dont_say: [...(loaded.dont_say ?? [])],
      banned_terms: [...(loaded.banned_terms ?? [])],
      signature_phrases: [...(loaded.signature_phrases ?? [])],
      updated_at: loaded.updated_at,
      updated_by: loaded.updated_by,
    });
    setError(null);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canEdit || toneOver) return;
    setError(null);
    try {
      await updateMutation.mutateAsync({
        tone: draft.tone,
        do_say: draft.do_say,
        dont_say: draft.dont_say,
        banned_terms: draft.banned_terms,
        signature_phrases: draft.signature_phrases,
      });
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to save brand voice.";
      setError(message);
    }
  };

  return (
    <div className="page" style={{ maxWidth: 1000 }}>
      <PageHeader
        label="Content Generation"
        title="Brand Voice"
        description="Shared brand voice settings injected into every content generation prompt."
      />

      <PermissionGate resource="brand_voice" action="view">
        <form onSubmit={handleSubmit}>
          <div className="card">
            <div className="card-b">
              <div className="sec">
                <div className="sec-h">
                  <b>
                    <span className="sec-n">1</span>Tone
                  </b>
                  <span>
                    Injected into the system prompt for every content generation.
                  </span>
                </div>
                <div className="fg">
                  <div className="lbl-row">
                    <label className="fl">Tone</label>
                    <span className={`cnt${toneOver ? " warn" : ""}`}>
                      {toneBytes.toLocaleString()} /{" "}
                      {TONE_MAX_BYTES.toLocaleString()} B
                    </span>
                  </div>
                  <textarea
                    className="ta"
                    rows={5}
                    value={draft.tone}
                    onChange={(e) =>
                      setDraft((prev) => ({ ...prev, tone: e.target.value }))
                    }
                    placeholder="e.g. professional, warm, encouraging; never make medical claims."
                  />
                  <p className="hint">
                    Describes the overall voice. Injected into the system prompt
                    for every content generation.
                  </p>
                </div>
              </div>

              <div className="sec">
                <div className="sec-h">
                  <b>
                    <span className="sec-n">2</span>Vocabulary
                  </b>
                  <span>Phrase lists the generator applies to every output.</span>
                </div>

                <div className="row c2">
                  <ChipListEditor
                    label="Do say"
                    values={draft.do_say}
                    onChange={(next) =>
                      setDraft((prev) => ({ ...prev, do_say: next }))
                    }
                    placeholder="Add a phrase to encourage"
                  />

                  <ChipListEditor
                    label="Don't say"
                    values={draft.dont_say}
                    onChange={(next) =>
                      setDraft((prev) => ({ ...prev, dont_say: next }))
                    }
                    placeholder="Add a phrase to avoid"
                  />
                </div>

                <div className="divider" />

                <div className="row c2">
                  <ChipListEditor
                    label="Banned terms"
                    danger
                    hint="Case-insensitive substring match. Generations containing any banned term will be blocked."
                    values={draft.banned_terms}
                    onChange={(next) =>
                      setDraft((prev) => ({ ...prev, banned_terms: next }))
                    }
                    placeholder="e.g. Dream Quest"
                  />

                  <ChipListEditor
                    label="Signature phrases"
                    values={draft.signature_phrases}
                    onChange={(next) =>
                      setDraft((prev) => ({ ...prev, signature_phrases: next }))
                    }
                    placeholder="A sign-off the assistant can reuse"
                  />
                </div>
              </div>
            </div>

            <div className="card-f">
              <span className="meta">
                {isLoading ? (
                  "Loading..."
                ) : loaded?.updated_at ? (
                  <>
                    Last updated by{" "}
                    <b style={{ color: "var(--ink-2)" }}>
                      {loaded.updated_by ?? "unknown"}
                    </b>{" "}
                    at {new Date(loaded.updated_at).toLocaleString()}
                  </>
                ) : (
                  "No saved brand voice yet."
                )}
              </span>
              <div className="r">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleReset}
                  disabled={updateMutation.isPending}
                >
                  Reset
                </Button>
                <PermissionGate
                  resource="brand_voice"
                  action="edit"
                  fallback={
                    <Button type="button" disabled>
                      Save
                    </Button>
                  }
                >
                  <Button
                    type="submit"
                    loading={updateMutation.isPending}
                    disabled={toneOver || updateMutation.isPending}
                  >
                    Save Brand Voice
                  </Button>
                </PermissionGate>
              </div>
            </div>

            {error && (
              <div style={{ padding: "0 16px 14px" }}>
                <div className="al e">
                  <Icon name="alert" />
                  <div className="cnt-x">{error}</div>
                </div>
              </div>
            )}
          </div>
        </form>
      </PermissionGate>
    </div>
  );
};

export default BrandVoice;
